import { Card } from "primereact/card";
import React, { useEffect, useState } from "react";
import { Button, Modal, Select, Textfield } from "../../../../components/core";
import PartnerAccessPermissionDialog from "./access-permission-configuration";
import { Divider } from "primereact/divider";
import { Strings } from "../../../../constants/strings";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { SEARCH_PARTNERS } from "../../../../constants/graphql/partners-query";
import { UPSERT_PARTNER_ADMIN_ACCOUNT } from "../../../../constants/graphql/partner-admin-account";
import { hasExistingAdminAccount } from "..";
import { useSelector } from "react-redux";
import PartnerPermissionDisplayDialog from "./partner-permission-display";
import { partnerSidebarItems } from "./constants/sidebarItems";

export const PARTNER_STATUS = [
  {
    name: "Active",
    value: 1,
  },
  {
    name: "Inactive",
    value: 2,
  },
];

const PartnerAdminAccount = ({ setActiveIndex }) => {
  const accessPermission = useSelector(
    (state) => state.accessPermissions.accessPermissions
  );
  const navigate = useNavigate();
  const { partnerId, operatorId } = useParams();
  const [accountVars, setAccountVars] = useState({
    username: "",
    email: "",
    mobileNumber: "",
    password: "",
    active: null,
    status: "",
    adminAccess: "",
    accessPermissions: "",
    partnerId: null,
    operatorId: null,
    company: "",
    companyNickname: "",
    partnerAccountId: null,
  });

  // State for managing individual account cards
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [accountChanges, setAccountChanges] = useState({});

  // Track which account is being edited for permissions
  const [editingPermissionsFor, setEditingPermissionsFor] = useState(null);

  const [isAlreadyExisting, setIsAlreadyExisting] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [permissionStats, setPermissionStats] = useState({
    total: 0,
    enabled: 0,
    byLevel: {
      noAccess: 0,
      viewOnly: 0,
      fullAccess: 0,
      approveReject: 0,
    },
    isConfigured: false,
  });

  // gql
  const searchPartner = useQuery(SEARCH_PARTNERS, {
    variables: {
      operatorId: operatorId,
      partnerId: parseInt(partnerId),
    },
  });
  const [upsertPartnerAdminAccount, { loading: upsertLoading }] = useMutation(
    UPSERT_PARTNER_ADMIN_ACCOUNT
  );
  const [originalEmail, setOriginalEmail] = useState("");
  const [verifEmail, setVerifEmail] = useState("");
  const [verifPassword, setVerifPassword] = useState("");
  const [showConfigurationDialog, setShowConfigurationDialog] = useState(false);
  const [validation, setValidation] = React.useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "Invalid data",
    message: "",
    open: false,
  });

  const partnerAccounts =
    searchPartner.data?.searchCouriers[0]?.partnerAccounts?.filter(
      (account) => account.adminAccess === true
    ) || [];

  useEffect(() => {
    if (searchPartner.data && searchPartner.data.searchCouriers[0]) {
      const partnerData = searchPartner.data.searchCouriers[0];
      const baseVars = {
        partnerId: partnerId,
        operatorId: operatorId,
        companyNickname: partnerData.companyNickname,
        company: partnerData.operator.company,
      };
      if (
        isAlreadyExisting &&
        partnerData.partnerAccounts &&
        partnerData.partnerAccounts.length > 0
      ) {
        const existingAdminAccount = partnerData.partnerAccounts.find(
          (account) => account.adminAccess === true
        );
        if (existingAdminAccount) {
          const existingEmail =
            existingAdminAccount.email || existingAdminAccount.userEmail || "";
          setOriginalEmail(existingEmail);
          setAccountVars({
            ...baseVars,
            username: existingAdminAccount.username || "",
            email: existingEmail,
            mobileNumber:
              existingAdminAccount.mobileNumber ||
              existingAdminAccount.userMobile?.replace("+63", "") ||
              "",
            password: "",
            active: existingAdminAccount.active,
            status: existingAdminAccount.active ? 1 : 2,
            adminAccess: existingAdminAccount.adminAccess,
            accessPermissions: JSON.parse(
              existingAdminAccount.accessPermissions
            ),
            // ? typeof existingAdminAccount.accessPermissions === "string"
            //   ? JSON.parse(existingAdminAccount.accessPermissions)
            //   : existingAdminAccount.accessPermissions
            // : "",
            partnerAccountId: existingAdminAccount.partnerAccountId,
          });
        }
      } else {
        setAccountVars({
          ...accountVars,
          ...baseVars,
        });
      }
    }
  }, [partnerId, operatorId, searchPartner.data, isAlreadyExisting]);

  const handleChange = (e, field) => {
    if (field === "accessPermissions") {
      setAccountVars({
        ...accountVars,
        accessPermissions: e,
      });
      setShowConfigurationDialog(false);
      return;
    }
    const value = e.target.value;
    setAccountVars({
      ...accountVars,
      [field]: value,
    });
    setChangesMade(true);
  };

  // Handle changes for individual account cards
  const handleAccountChange = (accountId, field, value) => {
    setAccountChanges((prev) => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [field]: value,
      },
    }));
  };

  const getValidPermissionTags = () => {
    const validTags = new Set();

    partnerSidebarItems.forEach((section) => {
      // Add standalone page tags
      if (!section.content) {
        validTags.add(section.tag);
      } else {
        // Add content page tags
        section.content.forEach((page) => {
          validTags.add(page.tag);
        });
      }
    });

    // Add leeway tags that are always valid
    validTags.add("dashboard");

    return validTags;
  };

  // Add this cleanup function
  const cleanupAccessPermissions = (permissions) => {
    if (!permissions) return {};

    const validTags = getValidPermissionTags();
    const cleaned = {};

    // Only include permissions that have valid tags
    Object.entries(permissions).forEach(([tag, level]) => {
      if (validTags.has(tag)) {
        cleaned[tag] = level;
      }
    });

    return cleaned;
  };

  const handleSavePermissions = (updatedPermissions) => {
    const cleanedPermissions = cleanupAccessPermissions(updatedPermissions);

    // If editing an existing account, save to accountChanges
    if (editingPermissionsFor) {
      handleAccountChange(
        editingPermissionsFor,
        "accessPermissions",
        cleanedPermissions
      );
    } else {
      // If creating new account, save to main accountVars
      handleChange(cleanedPermissions, "accessPermissions");
    }

    setShowConfigurationDialog(false);
    setEditingPermissionsFor(null);
  };

  // Open permission dialog for specific account
  const openPermissionDialog = (account) => {
    setEditingPermissionsFor(account.partnerAccountId);

    // Prepare account data for the dialog
    const accountData = {
      ...accountVars,
      partnerAccountId: account.partnerAccountId,
      username: account.username,
      accessPermissions:
        accountChanges[account.partnerAccountId]?.accessPermissions ||
        (account.accessPermissions
          ? typeof account.accessPermissions === "string"
            ? JSON.parse(account.accessPermissions)
            : account.accessPermissions
          : ""),
    };

    setAccountVars(accountData);
    setShowConfigurationDialog(true);
  };

  const toggleAccountExpand = (accountId) => {
    setExpandedAccounts((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  const handleSaveAccount = (account) => {
    const changes = accountChanges[account.partnerAccountId] || {};

    const variables = {
      partnerId: parseInt(partnerId),
      operatorId: parseInt(operatorId),
      partnerAccountId: parseInt(account.partnerAccountId),
      adminAccess:
        changes.adminAccess !== undefined
          ? changes.adminAccess
          : account.adminAccess,
      active: changes.active !== undefined ? changes.active : account.active,
      username: account.username,
      userEmail: account.userEmail || account.email,
      userMobile: account.userMobile || account.mobile,
    };

    if (changes.accessPermissions) {
      variables.accessPermissions = JSON.stringify(changes.accessPermissions);
    }

    console.log({ changes, variables });

    upsertPartnerAdminAccount({
      variables: { input: variables },
    })
      .then((response) => {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
          title: Strings.popup_success_title_default,
          message: `Account ${account.username} updated successfully`,
          open: true,
          actions: (
            <div className="grid grid-cols-1 gap-2">
              <Button
                value={"OK"}
                className={"p-2"}
                onClick={() => {
                  setValidation({ open: false });
                  searchPartner.refetch();
                  setAccountChanges((prev) => {
                    const newChanges = { ...prev };
                    delete newChanges[account.partnerAccountId];
                    return newChanges;
                  });
                  setExpandedAccounts((prev) => ({
                    ...prev,
                    [account.partnerAccountId]: false,
                  }));
                }}
              />
            </div>
          ),
        });
      })
      .catch((error) => {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: Strings.popup_error_title,
          message: error.message || "Failed to update account",
          open: true,
          actions: (
            <div className="grid grid-cols-1 gap-2">
              <Button
                value={"OK"}
                className={"p-2"}
                onClick={() => setValidation({ open: false })}
              />
            </div>
          ),
        });
      });
  };

  const showValidationError = (message) => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title: Strings.popup_error_title,
      message,
      open: true,
      actions: (
        <div className="grid grid-cols-1 gap-2">
          <Button
            value={"OK"}
            className={"p-2"}
            onClick={() => setValidation({ open: false })}
          />
        </div>
      ),
    });
  };

  // Add this helper function
  const validateAccessPermissions = (accessPermissions) => {
    if (!accessPermissions || Object.keys(accessPermissions).length === 0) {
      return {
        isValid: false,
        message: "Access permissions must be configured",
      };
    }

    const perms =
      typeof accessPermissions === "string"
        ? JSON.parse(accessPermissions)
        : accessPermissions;

    console.log({ perms });

    // Valid permission values
    const validLevels = ["0", "1", "2", "5"];

    // Check each permission
    for (const [tag, level] of Object.entries(perms)) {
      if (!validLevels.includes(String(level))) {
        return {
          isValid: false,
          message: `Invalid permission level for "${tag}". Must be one of: No Access (0), Read Only (1), Read & Write (2), or Approve/Reject (5).`,
        };
      }
    }

    return {
      isValid: true,
      message: "Permissions are valid",
    };
  };

  // Then update handleValidateFields:
  const handleValidateFields = () => {
    const emailRegex = /^[a-zA-Z0-9._%+-ñÑ]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isPasswordRequired = !isAlreadyExisting;
    const hasEmailChanged =
      !isAlreadyExisting || accountVars.email !== originalEmail;

    // ✅ Validate access permissions first
    const permissionValidation = validateAccessPermissions(
      accountVars.accessPermissions
    );

    console.log({ permissionValidation });
    if (!permissionValidation.isValid) {
      return showValidationError(permissionValidation.message);
    }

    if (
      !accountVars.username ||
      !accountVars.email ||
      !accountVars.mobileNumber ||
      (isPasswordRequired && !accountVars.password) ||
      !accountVars.accessPermissions ||
      !accountVars.status
    ) {
      return showValidationError(Strings.popup_error_message_empty_field);
    } else if (accountVars.email && !emailRegex.test(accountVars.email)) {
      return showValidationError("Invalid email address format.");
    } else if (hasEmailChanged && accountVars.email !== verifEmail) {
      return showValidationError("The email you provided does not match");
    } else if (accountVars.password && accountVars.password !== verifPassword) {
      return showValidationError("The password you provided does not match");
    } else if (accountVars.mobileNumber < 9) {
      return showValidationError("Invalid mobile number format");
    } else {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
        title: Strings.popup_confirm_save_title,
        message: isAlreadyExisting
          ? "Are you sure you want to update this superadmin account?"
          : Strings.popup_confirm_save_massage,
        open: true,
        actions: (
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              onClick={() => {
                setValidation({
                  open: false,
                });
              }}
              disabled={upsertLoading}
              value={"No"}
              className={"p-2"}
            />
            <Button
              value={"Yes"}
              className={"p-2"}
              loading={upsertLoading}
              onClick={handleSaveChanges}
            />
          </div>
        ),
      });
    }
  };

  const buildVariables = () => {
    let updatedVariables = {};
    updatedVariables.partnerId = parseInt(accountVars.partnerId);
    updatedVariables.operatorId = parseInt(accountVars.operatorId);
    updatedVariables.adminAccess = true;

    if (isAlreadyExisting && accountVars.partnerAccountId) {
      updatedVariables.partnerAccountId = parseInt(
        accountVars.partnerAccountId
      );
    }
    if (accountVars.username !== "") {
      updatedVariables.username = accountVars.username;
    }
    if (accountVars.email !== "") {
      updatedVariables.userEmail = accountVars.email;
    }
    if (accountVars.password !== "") {
      updatedVariables.password = accountVars.password;
    }
    if (accountVars.status !== -1) {
      updatedVariables.active =
        accountVars.status === 1 || accountVars.status === "1" ? true : false;
    }
    if (accountVars.mobileNumber !== "") {
      let formattedMobile =
        accountVars.mobileNumber.charAt(0) === "0"
          ? "+63" + accountVars.mobileNumber.slice(1)
          : "+63" + accountVars.mobileNumber;
      updatedVariables.userMobile = formattedMobile;
    }
    if (accountVars.accessPermissions !== "") {
      const cleanedPermissions = cleanupAccessPermissions(
        accountVars.accessPermissions
      );
      updatedVariables.accessPermissions = JSON.stringify(cleanedPermissions);
    }
    return updatedVariables;
  };

  const handleSaveChanges = () => {
    const variables = buildVariables();
    upsertPartnerAdminAccount({
      variables: { input: variables },
    })
      .then((response) => {
        if (response.data) {
          setValidation({
            icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
            title: Strings.popup_success_title_default,
            message: isAlreadyExisting
              ? "Partner superadmin account updated successfully"
              : "Partner superadmin account created successfully",
            open: true,
            actions: (
              <div className="grid grid-cols-1 gap-2">
                <Button
                  value={"OK"}
                  className={"p-2"}
                  onClick={() => {
                    navigate(
                      `/partners/partner-profile/${operatorId}/${partnerId}`,
                      { replace: true }
                    );
                    window.location.reload();
                  }}
                />
              </div>
            ),
          });
        }
      })
      .catch((error) => {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: Strings.popup_error_title,
          message: error.message || "Failed to save superadmin account",
          open: true,
          actions: (
            <div className="grid grid-cols-1 gap-2">
              <Button
                value={"OK"}
                className={"p-2"}
                onClick={() => setValidation({ open: false })}
              />
            </div>
          ),
        });
      });
  };

  useEffect(() => {
    if (searchPartner.data) {
      var searchPartnerData = searchPartner.data.searchCouriers[0];
      if (hasExistingAdminAccount(searchPartnerData.partnerAccounts)) {
        setIsAlreadyExisting(true);
      } else {
        setIsAlreadyExisting(false);
      }
    }
  }, [searchPartner.data]);

  const getConfiguredPermissionsCount = (permissions) => {
    if (!permissions || permissions === "") return { total: 0, byLevel: {} };
    const perms =
      typeof permissions === "string" ? JSON.parse(permissions) : permissions;
    if (!perms) return { total: 0, byLevel: {} };
    const permissionsObj = perms;
    const byLevel = {
      noAccess: 0,
      viewOnly: 0,
      fullAccess: 0,
      approveReject: 0,
    };
    Object.values(permissionsObj).forEach((value) => {
      if (value === "0") byLevel.noAccess++;
      else if (value === "1") byLevel.viewOnly++;
      else if (value === "2") byLevel.fullAccess++;
      else if (value === "5") byLevel.approveReject++;
    });
    return {
      total: Object.keys(permissionsObj).length,
      enabled: byLevel.viewOnly + byLevel.fullAccess + byLevel.approveReject,
      byLevel,
    };
  };

  // useEffect(() => {
  //   const calculatePermissionStats = () => {
  //     const hasPermissions =
  //       accountVars.accessPermissions &&
  //       accountVars.accessPermissions !== "" &&
  //       accountVars.accessPermissions !== "{}";
  //     if (!hasPermissions) {
  //       setPermissionStats({
  //         total: 0,
  //         enabled: 0,
  //         byLevel: {
  //           noAccess: 0,
  //           viewOnly: 0,
  //           fullAccess: 0,
  //           approveReject: 0,
  //         },
  //         isConfigured: false,
  //       });
  //       return;
  //     }
  //     const stats = getConfiguredPermissionsCount(
  //       accountVars.accessPermissions
  //     );
  //     setPermissionStats({ ...stats, isConfigured: true });
  //   };
  //   calculatePermissionStats();
  // }, [accountVars.accessPermissions]);

  useEffect(() => {
    const calculatePermissionStats = () => {
      let permissionsToCheck = accountVars.accessPermissions;

      const hasPermissions =
        permissionsToCheck &&
        permissionsToCheck !== "" &&
        permissionsToCheck !== "{}";

      if (!hasPermissions) {
        setPermissionStats({
          total: 0,
          enabled: 0,
          byLevel: {
            noAccess: 0,
            viewOnly: 0,
            fullAccess: 0,
            approveReject: 0,
          },
          isConfigured: false,
        });
        return;
      }
      // Filter permissions to only show pages current user has access to
      const filteredPermissions = getAccessiblePermissions(
        permissionsToCheck,
        accessPermission
      );

      const stats = getConfiguredPermissionsCount(filteredPermissions);
      setPermissionStats({ ...stats, isConfigured: true });
    };

    calculatePermissionStats();
  }, [accountVars.accessPermissions, accessPermission]);

  // Helper function to get intersection of permissions
  const getAccessiblePermissions = (
    partnerPermissions,
    currentUserPermissions
  ) => {
    const perms =
      typeof partnerPermissions === "string"
        ? JSON.parse(partnerPermissions)
        : partnerPermissions;

    if (!perms) return {};

    // Only include pages where current user has access (value !== "0")
    const filtered = {};
    Object.entries(perms).forEach(([key, value]) => {
      if (currentUserPermissions[key] && currentUserPermissions[key] !== "0") {
        filtered[key] = value;
      }
    });

    return filtered;
  };

  const getAccountPermissions = () => {
    const account = partnerAccounts.find(
      (acc) => acc.partnerAccountId === editingPermissionsFor
    );

    console.log({ account, partnerAccounts });
    return account?.accessPermissions || {};
  };
  return (
    <div className="flex flex-col gap-4">
      {/* ALL PARTNER ACCOUNTS CARDS */}
      {partnerAccounts.length > 0 ? (
        <div className="mt-6">
          <Card>
            <div className="font-bold text-xl mb-4">
              All Partner Accounts ({partnerAccounts.length})
            </div>
            <Divider />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {partnerAccounts.map((account) => {
                const currentChanges =
                  accountChanges[account.partnerAccountId] || {};
                const isExpanded = expandedAccounts[account.partnerAccountId];
                const hasChanges = Object.keys(currentChanges).length > 0;

                return (
                  <Card
                    key={account.partnerAccountId}
                    className={`border-2 ${
                      account.active ? "border-l-green-500" : "border-l-red-500"
                    } ${account.adminAccess ? "bg-purple-50" : "bg-white"}`}
                  >
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <i
                          className={`pi ${
                            account.adminAccess ? "pi-shield" : "pi-user"
                          } text-lg ${
                            account.adminAccess
                              ? "text-purple-600"
                              : "text-blue-600"
                          }`}
                        ></i>
                        <div>
                          <div className="font-bold text-lg">
                            {account.username}
                          </div>
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              account.adminAccess
                                ? "bg-purple-200 text-purple-800"
                                : "bg-blue-200 text-blue-800"
                            }`}
                          >
                            {account.adminAccess ? "Super Admin" : "User"}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col items-end">
                        <i
                          className={`pi ${
                            account.active
                              ? "pi-check-circle"
                              : "pi-times-circle"
                          } ${
                            account.active ? "text-green-500" : "text-red-500"
                          }`}
                        ></i>
                        <span
                          className={`text-xs font-medium ${
                            account.active ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {account.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    <Divider />

                    {/* Account Details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <i className="pi pi-envelope text-gray-400"></i>
                        <span className="truncate">
                          {account.userEmail || account.email}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <i className="pi pi-phone text-gray-400"></i>
                        <span>{account.userMobile || account.mobile}</span>
                      </div>
                      {/* 
                      <div className="text-xs text-gray-500 pt-2 border-t">
                        Account ID: {account.partnerAccountId}
                      </div> */}
                    </div>

                    {/* Expandable Edit Section */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div>
                          <label className="text-sm font-medium">
                            Account Status:
                          </label>
                          <Select
                            items={PARTNER_STATUS}
                            value={
                              currentChanges.active !== undefined
                                ? currentChanges.active
                                  ? 1
                                  : 2
                                : account.active
                                ? 1
                                : 2
                            }
                            onChange={(e) =>
                              handleAccountChange(
                                account.partnerAccountId,
                                "active",
                                e.target.value === 1 || e.target.value === "1"
                              )
                            }
                            className="w-full mt-1"
                            disabled={
                              accessPermission.partners === 1 ||
                              accessPermission.partners === "1"
                            }
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium mb-2 block">
                            Partner Access Permissions:
                          </label>
                          <Button
                            value="Change Partner Account Permission"
                            // icon="pi pi-cog"
                            className="w-full p-2 bg-primary-primaryYellow text-sm"
                            onClick={() => openPermissionDialog(account)}
                            disabled={
                              accessPermission.partners === 1 ||
                              accessPermission.partners === "1"
                            }
                          />

                          {currentChanges.accessPermissions && (
                            <div className="mt-2 text-xs text-green-600 bg-green-50 p-2 rounded">
                              <i className="pi pi-check mr-1"></i>
                              Permissions updated (unsaved)
                            </div>
                          )}
                        </div>

                        {hasChanges && (
                          <div className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                            <i className="pi pi-exclamation-triangle mr-1"></i>
                            Unsaved changes
                          </div>
                        )}
                      </div>
                    )}

                    {/* Card Actions */}
                    <div className="mt-4 flex gap-2">
                      <Button
                        value={isExpanded ? "Cancel" : "Edit"}
                        // icon={`pi ${
                        //   isExpanded ? "pi-chevron-up" : "pi-chevron-down"
                        // }`}
                        className={`flex-1 p-2  text-secondary-purpleGray ${
                          isExpanded
                            ? "bg-white border border-primary-primaryYellow"
                            : ""
                        }`}
                        onClick={() =>
                          toggleAccountExpand(account.partnerAccountId)
                        }
                        disabled={
                          accessPermission.partners === 1 ||
                          accessPermission.partners === "1"
                        }
                      />

                      {isExpanded && (
                        <Button
                          value="Save"
                          // icon="pi pi-save"
                          className="flex-1 p-2 bg-primary-primaryYellow"
                          // onClick={() => handleSaveAccount(account)}
                          onClick={() => {
                            setValidation({
                              icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
                              title: Strings.popup_confirm_save_title,
                              message: Strings.popup_confirm_save_massage,
                              open: true,
                              actions: (
                                <div className="grid grid-cols-2 gap-2">
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      setValidation({
                                        open: false,
                                      });
                                    }}
                                    value={"No"}
                                    className={"p-2"}
                                  />
                                  <Button
                                    value={"Yes"}
                                    className={"p-2"}
                                    onClick={() => {
                                      handleSaveAccount(account);
                                    }}
                                  />
                                </div>
                              ),
                            });
                          }}
                          disabled={
                            !hasChanges ||
                            accessPermission.partners === 1 ||
                            accessPermission.partners === "1"
                          }
                        />
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        </div>
      ) : (
        <Card>
          <div className="font-bold text-lg ">
            {isAlreadyExisting ? "Edit" : "Add"} Partner Superadmin Account
          </div>

          <div className="grid grid-cols-12">
            <div className="col-span-12">
              {/* Divider */}
              <div className=" px-4">
                <Divider className="border border-gray-200" />
              </div>
              <div className="col-span-12">
                <div className="grid grid-cols-12 items-center">
                  {" "}
                  <div className="col-span-2">
                    <i className="text-red-500 mr-2">*</i>Partner Access
                    Permission:
                  </div>
                  <div className="col-span-6">
                    <Button
                      value={`Configure Partner Access Permission`}
                      className={`p-2 bg-primary-primaryYellow`}
                      onClick={() => {
                        setShowConfigurationDialog(true);
                      }}
                    />
                  </div>
                  <div className="col-span-4">
                    {!permissionStats.isConfigured ? (
                      <div className="flex items-center gap-2 text-red-600">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-sm font-medium">
                          Not Configured
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <div
                          className={`flex items-center gap-2 ${
                            permissionStats.total > 0
                              ? "text-green-600"
                              : "text-yellow-600"
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d={
                                permissionStats.total > 0
                                  ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  : "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              }
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {permissionStats.total > 0
                              ? "Configured"
                              : "Configured (All Disabled)"}
                          </span>
                        </div>

                        <div className="ml-8 text-xs text-gray-600">
                          <div className="font-medium">
                            {permissionStats.total} permission
                            {permissionStats.total !== 1 ? "s" : ""} configured
                          </div>
                          <div className="mt-1 space-y-0.5">
                            {permissionStats.byLevel.noAccess > 0 && (
                              <div className="text-gray-500">
                                • {permissionStats.byLevel.noAccess} no access
                              </div>
                            )}
                            {permissionStats.byLevel.viewOnly > 0 && (
                              <div className="text-blue-600">
                                • {permissionStats.byLevel.viewOnly} read only
                              </div>
                            )}
                            {permissionStats.byLevel.fullAccess > 0 && (
                              <div className="text-green-600">
                                • {permissionStats.byLevel.fullAccess} read &
                                write
                              </div>
                            )}
                            {permissionStats.byLevel.approveReject > 0 && (
                              <div className="text-purple-600">
                                • {permissionStats.byLevel.approveReject} full
                                access
                              </div>
                            )}
                          </div>
                          {permissionStats.total === 0 && (
                            <div className="text-yellow-600 text-xs mt-1">
                              ⚠️ All permissions are currently disabled
                            </div>
                          )}
                        </div>

                        {/* <button
                          onClick={() => setShowConfigurationDialog(true)}
                          className="text-xs text-blue-600 hover:underline ml-8 text-left w-fit"
                        >
                          View/Edit details
                        </button> */}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-8 px-4">
                <Divider className="border border-gray-200" />
              </div>
            </div>

            <div className="col-span-6">
              <div className="grid grid-cols-10 space-y-3">
                <div className="font-bold col-span-10 pt-6">
                  Superadmin Login Credentials
                </div>

                <div className="flex flex-col col-span-10">
                  <label>
                    <span className="text-red-500">* </span>Superadmin Email
                  </label>
                  <Textfield
                    maxLength={128}
                    disabled={
                      accessPermission.partners === 1 ||
                      accessPermission.partners === "1"
                    }
                    className="w-full"
                    onChange={(e) => handleChange(e, "email")}
                    value={accountVars.email}
                  />
                </div>

                {/* Show confirm email only if email has changed from original or is a new account */}
                {accountVars.email &&
                  (!isAlreadyExisting ||
                    accountVars.email !== originalEmail) && (
                    <div className="flex flex-col col-span-10">
                      <label>
                        <span className="text-red-500">* </span>Confirm
                        Superadmin Email
                      </label>
                      <Textfield
                        maxLength={128}
                        className="w-full"
                        onChange={(e) => setVerifEmail(e.target.value)}
                        value={verifEmail}
                      />
                    </div>
                  )}

                <div
                  className={`${
                    isAlreadyExisting ? "hidden" : "flex flex-col col-span-10"
                  }`}
                >
                  <label>
                    <span className="text-red-500">* </span>
                    Superadmin Password
                  </label>
                  <Textfield
                    type={"password"}
                    className="w-full"
                    onChange={(e) => handleChange(e, "password")}
                    value={accountVars.password}
                    placeholder={
                      isAlreadyExisting ? "Enter new password to change" : ""
                    }
                  />
                </div>

                {accountVars.password && (
                  <div className="flex flex-col col-span-10">
                    <label>
                      <span className="text-red-500">* </span>Confirm Superadmin
                      Password
                    </label>
                    <Textfield
                      type={"password"}
                      className="w-full"
                      onChange={(e) => setVerifPassword(e.target.value)}
                      value={verifPassword}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="col-span-6"></div>

            <div className="col-span-12 pt-6">
              <div className="grid grid-cols-6 space-y-3">
                <div className="col-span-6 font-bold">Other Fields </div>

                <div className="col-span-6">
                  <div className="grid grid-cols-8 space-x-1">
                    {/* operator */}
                    <div className="col-span-4 space-y-3">
                      <div>
                        <div className="col-span-8">Operator</div>
                        <div className="col-span-8">
                          <Textfield
                            className="w-full p-inputtext-sm "
                            value={accountVars.company}
                            readOnly
                            disabled
                          />
                        </div>
                      </div>

                      <div>
                        <div>
                          <span className="text-red-500">* </span>Superadmin
                          Username
                        </div>
                        <Textfield
                          className="w-full p-inputtext-sm"
                          onChange={(e) => handleChange(e, "username")}
                          value={accountVars.username}
                          disabled={
                            accessPermission.partners === 1 ||
                            accessPermission.partners === "1"
                          }
                          maxLength={32}
                        />
                      </div>

                      <div>
                        <div>
                          <span className="text-red-500">* </span>Mobile Number
                        </div>
                        <div className="col-span-8">
                          <div className="flex gap-2">
                            <Textfield
                              className="text-center p-inputtext-sm w-[110px]"
                              placeholder={`+ 63`}
                              disabled
                            />
                            <Textfield
                              classNameParent={`w-full`}
                              className=" p-inputtext-sm w-full"
                              keyfilter={"int"}
                              maxLength={10}
                              disabled={
                                accessPermission.partners === 1 ||
                                accessPermission.partners === "1"
                              }
                              value={
                                accountVars.mobileNumber
                                  ? `9${accountVars.mobileNumber.substring(1)}`
                                  : ""
                              }
                              onChange={(e) => {
                                handleChange(e, "mobileNumber");
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-4 space-y-3">
                      <div>
                        <div className="col-span-8">Partner</div>
                        <div className="col-span-8">
                          <Textfield
                            className="w-full p-inputtext-sm "
                            value={accountVars.companyNickname}
                            readOnly
                            disabled
                          />
                        </div>
                      </div>

                      <div className="flex flex-col w-full">
                        <label>
                          <span className="text-red-500">* </span>Superadmin
                          Account Status
                        </label>
                        <Select
                          items={PARTNER_STATUS}
                          defaultOption={{
                            value: -1,
                            name: "-- Select superadmin account status --",
                          }}
                          value={accountVars.status}
                          disabled={
                            accessPermission.partners === 1 ||
                            accessPermission.partners === "1"
                          }
                          placeholder="Please select superadmin account status"
                          onChange={(e) => handleChange(e, "status")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-8 pt-4">
            <div className="grid grid-cols-8 gap-2">
              {/* Empty spacer when not already existing */}
              <div
                className={`${isAlreadyExisting ? "hidden" : "col-span-2"}`}
              ></div>

              {/* Message row */}
              <div className="col-span-8 flex w-full justify-between">
                <div></div>
                {isAlreadyExisting &&
                  !(
                    accessPermission.partners === 1 ||
                    accessPermission.partners === "1"
                  ) && (
                    <div>
                      <span className="text-[14px] text-component-underlineBlue italic">
                        Save changes first before going back
                      </span>
                    </div>
                  )}
              </div>

              {/* Action Buttons */}
              <Button
                type="button"
                // disabled={!changesMade}
                value={`${isAlreadyExisting ? "Cancel" : "Skip for now"}`}
                className={`${
                  accessPermission.partners === 1 ||
                  accessPermission.partners === "1"
                    ? "hidden "
                    : "p-2 col-span-1"
                }`}
                onClick={() => {
                  setValidation({
                    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
                    title: Strings.popup_confirm_cancel_title,
                    message: Strings.popup_confirm_cancel_massage,
                    open: true,
                    actions: (
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          onClick={() => {
                            setValidation({
                              open: false,
                            });
                          }}
                          value={"No"}
                          className={"p-2"}
                        />
                        <Button
                          value={"Yes"}
                          className={"p-2"}
                          onClick={() => {
                            navigate(`/partners`);
                          }}
                        />
                      </div>
                    ),
                  });
                }}
              />

              <Button
                type="submit"
                value={
                  isAlreadyExisting
                    ? "Update Super Admin Account"
                    : "Create Super Admin Account"
                }
                className="p-2 col-span-2"
                disabled={
                  !changesMade ||
                  accessPermission.partners === 1 ||
                  accessPermission.partners === "1"
                }
                onClick={() => {
                  handleValidateFields();
                }}
              />

              <div
                className={`${
                  accessPermission.partners === 1 ||
                  accessPermission.partners === "1"
                    ? ""
                    : "hidden"
                }`}
              ></div>
              {/* Next Page Button */}
              <div
                className={`${
                  // isAlreadyExisting
                  // ?
                  "items-end justify-end flex col-span-5"
                  // : "hidden"
                }`}
              >
                <Button
                  type="button"
                  // disabled={!isAlreadyExisting}
                  className="p-2 col-span-2 bg-primary-primaryYellowLight"
                  value="Previous Page"
                  size="small"
                  iconPos="left"
                  icon="pi pi-angle-double-left"
                  onClick={() => {
                    // goBack()
                    // setActiveIndex(1);
                    // navigate(`/partners/`);

                    if (!isAlreadyExisting) {
                      setValidation({
                        icon: `${process.env.PUBLIC_URL}/assets/images/warning.png`,
                        title: "Go Back?",
                        message:
                          "Are you sure you want to go back without creating a super admin account for this partner?",
                        open: true,
                        actions: (
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              type="button"
                              onClick={() => {
                                setValidation({
                                  open: false,
                                });
                              }}
                              value={"No"}
                              className={"p-2"}
                            />
                            <Button
                              value={"Yes"}
                              className={"p-2"}
                              onClick={() => {
                                // setActiveIndex(1);
                                navigate(
                                  `/partners/partner-profile/${operatorId}/${partnerId}`,
                                  { replace: true }
                                );
                                window.location.reload();
                              }}
                            />
                          </div>
                        ),
                      });
                    } else {
                      setActiveIndex(1);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* <PartnerAccessPermissionDialog
        visible={showConfigurationDialog}
        onClose={() => {
          setShowConfigurationDialog(false);
          setEditingPermissionsFor(null);
        }}
        isEditMode={true}
        partnerData={accountVars}
        handleOnChange={
          editingPermissionsFor ? handleAccountPermissionChange : handleChange
        }
        // ⭐ Pass the actual permissions object, not the Redux permissions
        initialPermissions={
          editingPermissionsFor
            ? // For editing existing account from partnerAccounts
              (() => {
                const unsavedChanges =
                  accountChanges[editingPermissionsFor]?.accessPermissions;

                // If there are unsaved changes, use those
                if (unsavedChanges) {
                  return typeof unsavedChanges === "string"
                    ? JSON.parse(unsavedChanges)
                    : unsavedChanges;
                }

                // Otherwise, get from the account data
                const account = partnerAccounts.find(
                  (acc) => acc.partnerAccountId === editingPermissionsFor
                );

                if (account?.accessPermissions) {
                  return typeof account.accessPermissions === "string"
                    ? JSON.parse(account.accessPermissions)
                    : account.accessPermissions;
                }

                return {};
              })()
            : // For creating/editing main superadmin account
              (() => {
                const perms = accountVars.accessPermissions;
                if (!perms) return {};

                return typeof perms === "string" ? JSON.parse(perms) : perms;
              })()
        }
        // Keep this for checking if user can edit
        currentUserPermissions={accessPermission}
      /> */}

      <PartnerPermissionDisplayDialog
        visible={showConfigurationDialog}
        accountAccessPermissions={getAccountPermissions()}
        onHide={() => {
          setEditingPermissionsFor(null);

          setShowConfigurationDialog(false);
        }}
        // handleOnChange={
        //   editingPermissionsFor ? handleAccountPermissionChange : handleChange
        // }
        onSavePermissions={handleSavePermissions}
      />

      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
    </div>
  );
};

export default PartnerAdminAccount;
